import React from 'react';
import { Link } from 'react-router-dom';
import { Car, MapPin, CheckCircle, ArrowRight } from 'lucide-react';

const ForDrivers = () => {
  const driverSteps = [
    { step: 1, title: 'Download the App', description: 'Download the Advectigroup app from the App Store or Google Play.' },
    { step: 2, title: 'Sign Up as a Driver', description: 'Create your driver profile in the app and complete the quick onboarding.' },
    { step: 3, title: 'Qualify', description: 'Complete 50 miles of driving with the app open in the background to qualify as an Advectigroup driver.' },
    { step: 4, title: 'Choose a Campaign', description: 'Pick from available campaigns that match your routes and preferred wrap type.' },
    { step: 5, title: 'Schedule Installation', description: 'Book a professional installation appointment for your full or partial wrap.' },
    { step: 6, title: 'Drive & Get Paid', description: 'Drive your normal routes around Columbus and receive weekly payments.' }
  ];

  const requirements = [
    'Valid driver\'s license',
    '18 years or older',
    'Rideshare or delivery driver (100+ hours of driving per month in the Columbus area)',
    'Car 2010 or newer',
    'Vehicle in good condition'
  ];  return (
    <div className="bg-cream-light" data-testid="for-drivers-page">
      {/* Hero */}
      <section className="hero-gradient py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-navy/10 px-4 py-2 rounded-full mb-6">
              <Car className="text-navy" size={20} />
              <span className="text-navy font-medium">For Drivers</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-navy leading-tight">
              Earn Money <span className="text-gold">While You Drive</span>
            </h1>
            <p className="mt-6 text-xl text-navy/70">
              Earn up to $8 per hour just by having our advertising on your vehicle
              in the Columbus area. Full wraps or partial wraps available.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/get-started"
                className="inline-flex items-center justify-center gap-2 bg-navy text-cream px-8 py-4 rounded-full text-lg font-semibold hover:bg-navy-dark transition-all pulse-cta"
                data-testid="driver-cta"
              >
                Sign Up Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* For Drivers Steps */}
      <section className="py-16 bg-white" data-testid="driver-steps">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-12 justify-center">
            <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center">
              <Car className="text-cream" size={28} />
            </div>
            <h2 className="text-3xl font-bold text-navy">Driver Process</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {driverSteps.map((item, index) => (
              <div key={item.step} className="relative">
                <div className="card-hover bg-cream p-6 rounded-2xl h-full">
                  <div className="w-10 h-10 bg-navy text-cream rounded-full flex items-center justify-center font-bold text-lg mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-navy mb-2">{item.title}</h3>
                  <p className="text-navy/70">{item.description}</p>
                </div>
                {index < driverSteps.length - 1 && (index + 1) % 3 !== 0 && (
                  <ArrowRight className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-navy/30" size={24} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings Highlight */}
      <section className="py-12 bg-navy" data-testid="earnings-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-center max-w-3xl mx-auto">
            <div>
              <p className="text-4xl md:text-5xl font-bold text-cream">$2.50</p>
              <p className="text-cream mt-2">Per Hour Avg</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-bold text-cream">$250</p>
              <p className="text-cream mt-2">Per Month Avg</p>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16 bg-cream" data-testid="requirements-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-navy text-center mb-10">Basic Requirements</h2>
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <ul className="space-y-4">
              {requirements.map((req, index) => (
                <li key={index} className="flex items-center gap-4">
                  <CheckCircle className="text-green-500 flex-shrink-0" size={24} />
                  <span className="text-navy text-lg">{req}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Mobile App Demo Video */}
      <section className="py-16 bg-white" id="app-demo" data-testid="driver-app-demo-section">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-navy">See the Driver App in Action</h2>
            <p className="mt-4 text-xl text-navy/70">Watch how our mobile app works for drivers</p>
          </div>
          <div className="max-w-xs mx-auto rounded-2xl overflow-hidden shadow-lg bg-white">
            <video
              className="w-full h-auto"
              autoPlay
              loop
              muted
              playsInline
              controls={false}
              preload="auto"
              ref={(el) => { if (el) { el.muted = true; el.play().catch(() => {}); } }}
              data-testid="driver-demo-video"
            >
              <source src="/videos/driver-demo.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-cream mb-4">
            Ready to Start Earning?
          </h2>
          <p className="text-xl text-cream mb-8">
            Join our team of drivers in Columbus and turn your commute into cash.
          </p>
          <Link
            to="/get-started"
            className="inline-flex items-center gap-3 bg-cream text-navy px-10 py-5 rounded-full text-xl font-bold hover:bg-cream-light transition-all pulse-cta"
            data-testid="driver-cta-final"
          >
            Get Started Now
          </Link>
          <p className="mt-6 text-cream">
            <MapPin className="inline mr-2" size={16} />
            Columbus Metropolitan Area
          </p>
        </div>
      </section>
    </div>
  );
};

export default ForDrivers;
